(() => {
  if (window.__watchvaBooksHomeLoaded) return;
  window.__watchvaBooksHomeLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(pointer: fine) and (hover: hover)');

  const headerOffset = () => {
    const header = document.querySelector('[data-wv-header]');
    return (header ? header.getBoundingClientRect().height : 0) + 14;
  };

  function scrollToTarget(target) {
    if (!target) return;
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerOffset());
    window.scrollTo({ top, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }


  function readPendingCheckoutState() {
    const purchase = document.querySelector('[data-wv-purchase]');
    const key = purchase?.dataset.wvCheckoutKey || 'watchva:too-much-in-my-head:v6';
    try {
      const state = JSON.parse(localStorage.getItem(key) || 'null');
      const fresh = state?.startedAt && Date.now() - state.startedAt <= 24 * 60 * 60 * 1000;
      if (!fresh || state?.completed || !state?.email || Number(state?.step) < 1) return null;
      return state;
    } catch (_) {
      return null;
    }
  }

  function updatePurchaseCtas() {
    const pending = readPendingCheckoutState();
    document.querySelectorAll('[data-wv-purchase-cta]').forEach((node) => {
      if (!node.dataset.wvDefaultLabel) node.dataset.wvDefaultLabel = node.textContent.trim();
      node.textContent = pending ? 'Continue checkout' : node.dataset.wvDefaultLabel;
    });
  }

  function initAnchors(root) {
    if (!root || root.dataset.wvAnchorsReady === 'true') return;
    root.dataset.wvAnchorsReady = 'true';

    root.querySelectorAll('a[href^="#"]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href === '#') return;
      link.addEventListener('click', (event) => {
        const target = document.querySelector(href);
        if (!target) return;
        event.preventDefault();
        if (href === '#watchva-purchase') {
          target.dispatchEvent(new CustomEvent('watchva:start-purchase'));
        }
        scrollToTarget(target);
      });
    });
  }

  function initHeader(root) {
    if (!root || root.dataset.wvReady === 'true') return;
    root.dataset.wvReady = 'true';

    const toggle = root.querySelector('[data-wv-action="toggle-menu"]');
    const menu = root.querySelector('[data-wv-mobile-menu]');
    let previousOverflow = '';

    const focusables = () => menu ? [...menu.querySelectorAll('a[href], button:not([disabled])')] : [];

    const setMenu = (open, restoreFocus = false) => {
      if (!toggle || !menu) return;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      menu.classList.toggle('is-open', open);
      menu.setAttribute('aria-hidden', open ? 'false' : 'true');
      root.classList.toggle('menu-open', open);

      if (open) {
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.setTimeout(() => focusables()[0]?.focus({ preventScroll: true }), 20);
      } else {
        document.body.style.overflow = previousOverflow;
        if (restoreFocus) toggle.focus({ preventScroll: true });
      }
    };

    toggle?.addEventListener('click', () => {
      setMenu(toggle.getAttribute('aria-expanded') !== 'true');
    });

    menu?.addEventListener('click', (event) => {
      if (event.target.closest('a')) setMenu(false);
    });

    const onKeydown = (event) => {
      if (toggle?.getAttribute('aria-expanded') !== 'true') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenu(false, true);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onResize = () => {
      if (window.innerWidth > 899 && toggle?.getAttribute('aria-expanded') === 'true') setMenu(false);
    };

    const onScroll = () => root.classList.toggle('is-scrolled', window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    root.addEventListener('keydown', onKeydown);

    root._wvDestroy = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      root.removeEventListener('keydown', onKeydown);
      if (toggle?.getAttribute('aria-expanded') === 'true') setMenu(false);
    };
  }

  function initReveal(root) {
    if (!root || root.dataset.wvRevealReady === 'true') return;
    root.dataset.wvRevealReady = 'true';
    const nodes = [...root.querySelectorAll('[data-wv-reveal]')];
    if (!nodes.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    root.classList.add('wv-js');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -5% 0px' });

    nodes.forEach((node) => observer.observe(node));
    root._wvRevealObserver = observer;
  }

  function initQuickStart(root) {
    if (!root || root.dataset.wvQuickReady === 'true') return;
    root.dataset.wvQuickReady = 'true';

    const tabs = [...root.querySelectorAll('[data-wv-quick-tab]')];
    const panels = [...root.querySelectorAll('[data-wv-quick-panel]')];
    if (!tabs.length || !panels.length) return;

    const activate = (id, focusTab = false) => {
      const nextPanel = panels.find((panel) => panel.dataset.wvQuickPanel === id);
      const currentPanel = panels.find((panel) => !panel.hidden);
      if (!nextPanel) return;

      tabs.forEach((tab) => {
        const active = tab.dataset.wvQuickTab === id;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active && focusTab) tab.focus({ preventScroll: true });
      });

      panels.forEach((panel) => {
        if (panel !== nextPanel) {
          panel.hidden = true;
          panel.classList.remove('is-active');
        }
      });

      nextPanel.hidden = false;
      nextPanel.classList.add('is-active');

      if (!reduceMotion.matches && currentPanel !== nextPanel && nextPanel.animate) {
        nextPanel.animate(
          [
            { opacity: 0, transform: 'translateY(10px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          { duration: 360, easing: 'cubic-bezier(.2,.75,.2,1)' }
        );
      }
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab.dataset.wvQuickTab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        activate(tabs[next].dataset.wvQuickTab, true);
      });
    });

    const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    activate(selected.dataset.wvQuickTab);
  }

  function initPreviewRail(root) {
    if (!root || root.dataset.wvPreviewReady === 'true') return;
    root.dataset.wvPreviewReady = 'true';

    const rail = root.querySelector('[data-wv-preview-rail]');
    const cards = rail ? [...rail.querySelectorAll('[data-wv-preview-card]')] : [];
    const prev = root.querySelector('[data-wv-preview-prev]');
    const next = root.querySelector('[data-wv-preview-next]');
    const current = root.querySelector('[data-wv-preview-current]');
    const total = root.querySelector('[data-wv-preview-total]');
    const progress = root.querySelector('[data-wv-preview-progress]');
    const purchasePrompt = root.querySelector('[data-wv-preview-purchase-prompt]');
    const viewer = root.querySelector('[data-wv-preview-viewer]');
    const viewerImage = root.querySelector('[data-wv-viewer-image]');
    const viewerTitle = root.querySelector('[data-wv-viewer-title]');
    const viewerPage = root.querySelector('[data-wv-viewer-page]');
    const viewerCount = root.querySelector('[data-wv-viewer-count]');
    const viewerSection = root.querySelector('[data-wv-viewer-section]');
    const viewerStage = root.querySelector('[data-wv-viewer-stage]');
    const viewerCanvas = root.querySelector('[data-wv-viewer-canvas]');
    const viewerPrev = root.querySelector('[data-wv-viewer-prev]');
    const viewerNext = root.querySelector('[data-wv-viewer-next]');
    const closeButtons = viewer ? [...viewer.querySelectorAll('[data-wv-viewer-close]')] : [];
    if (!rail || !cards.length || !viewer || !viewerImage || !viewerStage || !viewerCanvas) return;

    let selectedIndex = 0;
    let viewerIndex = 0;
    let viewerOpen = false;
    let previousFocus = null;
    let previousOverflow = '';
    let scrollSettleTimer = 0;
    let programmaticUntil = 0;
    const viewedPages = new Set();
    const preloaded = new Set();

    let scale = 1;
    let translateX = 0;
    let translateY = 0;
    const pointers = new Map();
    let gestureStart = null;
    let pinchStart = null;
    let lastTapAt = 0;
    let lastTapX = 0;
    let lastTapY = 0;

    if (total) total.textContent = String(cards.length).padStart(2, '0');
    const clampIndex = (index) => Math.min(cards.length - 1, Math.max(0, index));
    const cardData = (index) => {
      const card = cards[clampIndex(index)];
      return {
        src: card.dataset.wvPreviewSrc || card.querySelector('img')?.src || '',
        page: card.dataset.wvPreviewPage || '',
        title: card.dataset.wvPreviewTitle || '',
        alt: card.dataset.wvPreviewAlt || card.querySelector('img')?.alt || 'Workbook page',
        section: card.dataset.wvPreviewSectionLabel || ''
      };
    };

    const updatePurchasePrompt = () => {
      if (!purchasePrompt || viewedPages.size < 2) return;
      purchasePrompt.hidden = false;
      if (!reduceMotion.matches && purchasePrompt.animate && !purchasePrompt.dataset.wvShown) {
        purchasePrompt.dataset.wvShown = 'true';
        purchasePrompt.animate(
          [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 320, easing: 'cubic-bezier(.2,.75,.2,1)' }
        );
      }
    };

    const setSelected = (index, { scroll = true, focus = false } = {}) => {
      selectedIndex = clampIndex(index);
      cards.forEach((card, cardIndex) => {
        const selected = cardIndex === selectedIndex;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-current', selected ? 'true' : 'false');
      });
      if (current) current.textContent = String(selectedIndex + 1).padStart(2, '0');
      if (progress) progress.style.transform = `scaleX(${(selectedIndex + 1) / cards.length})`;
      if (prev) prev.disabled = selectedIndex === 0;
      if (next) next.disabled = selectedIndex === cards.length - 1;

      if (scroll) {
        const card = cards[selectedIndex];
        const targetLeft = card.offsetLeft - Math.max(0, (rail.clientWidth - card.offsetWidth) / 2);
        const maxLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const clampedLeft = Math.min(maxLeft, Math.max(0, targetLeft));
        programmaticUntil = performance.now() + (reduceMotion.matches ? 80 : 700);
        rail.scrollTo({ left: clampedLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
      if (focus) cards[selectedIndex]?.focus({ preventScroll: true });
    };

    const mostVisibleIndex = () => {
      const railRect = rail.getBoundingClientRect();
      let bestIndex = selectedIndex;
      let bestRatio = -1;
      let bestDistance = Infinity;
      const center = railRect.left + railRect.width / 2;
      cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, railRect.right) - Math.max(rect.left, railRect.left));
        const ratio = visibleWidth / Math.max(1, rect.width);
        const distance = Math.abs((rect.left + rect.width / 2) - center);
        if (ratio > bestRatio + 0.01 || (Math.abs(ratio - bestRatio) <= 0.01 && distance < bestDistance)) {
          bestRatio = ratio;
          bestDistance = distance;
          bestIndex = index;
        }
      });
      return bestIndex;
    };

    const preloadIndex = (index) => {
      if (index < 0 || index >= cards.length || preloaded.has(index)) return;
      preloaded.add(index);
      const src = cardData(index).src;
      if (!src) return;
      const image = new Image();
      image.decoding = 'async';
      image.src = src;
    };

    const applyTransform = () => {
      viewerImage.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
      viewerCanvas.classList.toggle('is-zoomed', scale > 1.01);
    };

    const constrainPan = () => {
      const maxX = Math.max(0, viewerImage.clientWidth * (scale - 1) / 2);
      const maxY = Math.max(0, viewerImage.clientHeight * (scale - 1) / 2);
      translateX = Math.min(maxX, Math.max(-maxX, translateX));
      translateY = Math.min(maxY, Math.max(-maxY, translateY));
    };

    const resetZoom = () => {
      scale = 1;
      translateX = 0;
      translateY = 0;
      applyTransform();
    };

    const setZoom = (nextScale) => {
      scale = Math.min(3, Math.max(1, nextScale));
      if (scale === 1) {
        translateX = 0;
        translateY = 0;
      }
      constrainPan();
      applyTransform();
    };

    const renderViewer = (index, { syncRail = true } = {}) => {
      viewerIndex = clampIndex(index);
      const data = cardData(viewerIndex);
      resetZoom();
      viewerImage.src = data.src;
      viewerImage.alt = data.alt;
      if (viewerTitle) viewerTitle.textContent = data.title;
      if (viewerPage) viewerPage.textContent = data.page ? `PAGE ${data.page}` : 'WORKBOOK PAGE';
      if (viewerSection) viewerSection.textContent = data.section || 'WATCHVA';
      if (viewerCount) viewerCount.textContent = `${String(viewerIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
      if (viewerPrev) viewerPrev.disabled = viewerIndex === 0;
      if (viewerNext) viewerNext.disabled = viewerIndex === cards.length - 1;
      viewedPages.add(viewerIndex);
      preloadIndex(viewerIndex - 1);
      preloadIndex(viewerIndex + 1);
      if (syncRail) setSelected(viewerIndex, { scroll: true });
    };

    const viewerFocusables = () => [...viewer.querySelectorAll('button:not([disabled]), a[href]')].filter((node) => !node.hidden);

    const openViewer = (index, trigger) => {
      viewerOpen = true;
      previousFocus = trigger || document.activeElement;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      viewer.hidden = false;
      viewer.classList.add('is-open');
      renderViewer(index);
      requestAnimationFrame(() => root.querySelector('.wv-reader-close')?.focus({ preventScroll: true }));
    };

    const closeViewer = ({ restoreFocus = true } = {}) => {
      if (!viewerOpen) return;
      viewerOpen = false;
      viewer.classList.remove('is-open');
      viewer.hidden = true;
      document.body.style.overflow = previousOverflow;
      resetZoom();
      setSelected(viewerIndex, { scroll: true });
      updatePurchasePrompt();
      if (restoreFocus) previousFocus?.focus?.({ preventScroll: true });
    };

    const navigateViewer = (direction) => {
      const nextIndex = clampIndex(viewerIndex + direction);
      if (nextIndex === viewerIndex) return;
      renderViewer(nextIndex);
    };

    const settleFromManualScroll = () => {
      if (performance.now() < programmaticUntil) return;
      setSelected(mostVisibleIndex(), { scroll: false });
    };
    const onScroll = () => {
      window.clearTimeout(scrollSettleTimer);
      scrollSettleTimer = window.setTimeout(settleFromManualScroll, 120);
    };
    const onResize = () => {
      setSelected(selectedIndex, { scroll: true });
      if (viewerOpen) {
        constrainPan();
        applyTransform();
      }
    };
    const onRailKeydown = (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') setSelected(selectedIndex - 1, { focus: true });
      if (event.key === 'ArrowRight') setSelected(selectedIndex + 1, { focus: true });
      if (event.key === 'Home') setSelected(0, { focus: true });
      if (event.key === 'End') setSelected(cards.length - 1, { focus: true });
    };

    const onDocumentKeydown = (event) => {
      if (!viewerOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeViewer();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        navigateViewer(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateViewer(1);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = viewerFocusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const onPointerDown = (event) => {
      if (!viewerOpen || event.target.closest('button')) return;
      viewerStage.setPointerCapture?.(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 1) {
        gestureStart = { x: event.clientX, y: event.clientY, tx: translateX, ty: translateY, time: Date.now() };
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinchStart = { distance: Math.max(1, distance(a, b)), scale };
      }
    };
    const onPointerMove = (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2 && pinchStart) {
        const [a, b] = [...pointers.values()];
        setZoom(pinchStart.scale * (distance(a, b) / pinchStart.distance));
        return;
      }
      if (pointers.size === 1 && scale > 1.01 && gestureStart) {
        translateX = gestureStart.tx + (event.clientX - gestureStart.x);
        translateY = gestureStart.ty + (event.clientY - gestureStart.y);
        constrainPan();
        applyTransform();
      }
    };
    const onPointerUp = (event) => {
      const point = pointers.get(event.pointerId);
      pointers.delete(event.pointerId);
      if (!point) return;

      if (pointers.size < 2) pinchStart = null;
      if (gestureStart) {
        const dx = event.clientX - gestureStart.x;
        const dy = event.clientY - gestureStart.y;
        const elapsed = Date.now() - gestureStart.time;
        const isTap = elapsed < 320 && Math.abs(dx) < 12 && Math.abs(dy) < 12;

        if (scale <= 1.01 && elapsed < 500 && Math.abs(dx) > 58 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          navigateViewer(dx < 0 ? 1 : -1);
          lastTapAt = 0;
        } else if (isTap) {
          const now = Date.now();
          const nearLastTap = Math.hypot(event.clientX - lastTapX, event.clientY - lastTapY) < 36;
          if (now - lastTapAt < 340 && nearLastTap) {
            setZoom(scale > 1.01 ? 1 : 2);
            lastTapAt = 0;
          } else {
            lastTapAt = now;
            lastTapX = event.clientX;
            lastTapY = event.clientY;
          }
        }
      }
      if (pointers.size === 1) {
        const remaining = [...pointers.values()][0];
        gestureStart = { x: remaining.x, y: remaining.y, tx: translateX, ty: translateY, time: Date.now() };
      } else if (pointers.size === 0) {
        gestureStart = null;
      }
    };

    prev?.addEventListener('click', () => setSelected(selectedIndex - 1));
    next?.addEventListener('click', () => setSelected(selectedIndex + 1));
    cards.forEach((card, index) => card.addEventListener('click', () => openViewer(index, card)));
    viewerPrev?.addEventListener('click', () => navigateViewer(-1));
    viewerNext?.addEventListener('click', () => navigateViewer(1));
    closeButtons.forEach((button) => button.addEventListener('click', () => closeViewer()));
    rail.addEventListener('scroll', onScroll, { passive: true });
    rail.addEventListener('keydown', onRailKeydown);
    viewerStage.addEventListener('pointerdown', onPointerDown);
    viewerStage.addEventListener('pointermove', onPointerMove);
    viewerStage.addEventListener('pointerup', onPointerUp);
    viewerStage.addEventListener('pointercancel', onPointerUp);
    document.addEventListener('keydown', onDocumentKeydown);
    window.addEventListener('resize', onResize, { passive: true });

    setSelected(0, { scroll: false });

    root._wvPreviewDestroy = () => {
      window.clearTimeout(scrollSettleTimer);
      if (viewerOpen) closeViewer({ restoreFocus: false });
      rail.removeEventListener('scroll', onScroll);
      rail.removeEventListener('keydown', onRailKeydown);
      viewerStage.removeEventListener('pointerdown', onPointerDown);
      viewerStage.removeEventListener('pointermove', onPointerMove);
      viewerStage.removeEventListener('pointerup', onPointerUp);
      viewerStage.removeEventListener('pointercancel', onPointerUp);
      document.removeEventListener('keydown', onDocumentKeydown);
      window.removeEventListener('resize', onResize);
    };
  }

  function initFaq(root) {
    if (!root || root.dataset.wvFaqReady === 'true') return;
    root.dataset.wvFaqReady = 'true';

    root.querySelectorAll('[data-wv-faq-item]').forEach((details) => {
      const summary = details.querySelector('summary');
      const answer = details.querySelector('[data-wv-faq-answer]');
      if (!summary || !answer) return;

      let animation = null;
      let state = details.open ? 'open' : 'closed';

      const settle = (open) => {
        animation = null;
        state = open ? 'open' : 'closed';
        details.open = open;
        details.style.height = '';
        details.style.overflow = '';
      };

      const animate = (open) => {
        const startHeight = details.getBoundingClientRect().height || summary.getBoundingClientRect().height;
        animation?.cancel();

        if (open) details.open = true;
        const endHeight = open
          ? summary.getBoundingClientRect().height + answer.getBoundingClientRect().height
          : summary.getBoundingClientRect().height;

        if (reduceMotion.matches || !details.animate) {
          settle(open);
          return;
        }

        state = open ? 'opening' : 'closing';
        details.style.height = `${startHeight}px`;
        details.style.overflow = 'hidden';
        animation = details.animate(
          [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
          { duration: open ? 320 : 260, easing: open ? 'cubic-bezier(.2,.75,.2,1)' : 'cubic-bezier(.4,0,.2,1)' }
        );
        animation.onfinish = () => settle(open);
        animation.oncancel = () => { animation = null; };
      };

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        const shouldOpen = state === 'closed' || state === 'closing';
        animate(shouldOpen);
      });
    });
  }

  function initPurchase(root) {
    if (!root || root.dataset.wvPurchaseReady === 'true') return;
    root.dataset.wvPurchaseReady = 'true';

    const form = root.querySelector('form');
    const stages = [...root.querySelectorAll('[data-wv-checkout-stage]')];
    const successStage = root.querySelector('[data-wv-checkout-success]');
    const emailInput = root.querySelector('[data-wv-checkout-email]');
    const referenceInput = root.querySelector('[data-wv-checkout-reference]');
    const orderNodes = [...root.querySelectorAll('[data-wv-order-code]')];
    const emailSummary = root.querySelector('[data-wv-checkout-email-summary]');
    const hiddenOrder = root.querySelector('[data-wv-hidden-order]');
    const hiddenSubmitted = root.querySelector('[data-wv-hidden-submitted]');
    const hiddenBody = root.querySelector('[data-wv-hidden-body]');
    const checkoutMap = root.querySelector('[data-wv-checkout-map]');
    const checkoutMapSteps = checkoutMap ? [...checkoutMap.querySelectorAll('[data-wv-checkout-map-step]')] : [];
    const submitButton = root.querySelector('[data-wv-submit-payment]');
    const copyReturnButton = root.querySelector('[data-wv-copy-return-link]');
    const storageKey = root.dataset.wvCheckoutKey || 'watchva:checkout:v6';
    const ready = root.dataset.wvCheckoutReady === 'true';
    let formState = 'idle';
    const embeddedState = root.nextElementSibling?.matches?.('script[data-wv-purchase-state]') ? root.nextElementSibling : null;
    try {
      if (embeddedState) formState = JSON.parse(embeddedState.textContent || '{}').formState || 'idle';
    } catch (_) {}

    const maxAge = 24 * 60 * 60 * 1000;
    const readState = () => {
      try {
        const parsed = JSON.parse(localStorage.getItem(storageKey) || 'null');
        if (!parsed || !parsed.startedAt || Date.now() - parsed.startedAt > maxAge) {
          localStorage.removeItem(storageKey);
          return null;
        }
        return parsed;
      } catch (_) {
        return null;
      }
    };

    const saveState = (state) => {
      try { localStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) {}
    };

    const clearState = () => {
      try { localStorage.removeItem(storageKey); } catch (_) {}
    };

    const randomPart = (length = 4) => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      const bytes = new Uint8Array(length);
      if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
      else for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
      return [...bytes].map((byte) => chars[byte % chars.length]).join('');
    };

    const createOrderCode = () => {
      const now = new Date();
      const yy = String(now.getFullYear()).slice(-2);
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      return `WV-${yy}${mm}${dd}-${randomPart(4)}`;
    };

    let state = readState() || {
      orderCode: '',
      email: '',
      step: 0,
      startedAt: Date.now()
    };

    const ensureOrder = () => {
      if (!state.orderCode) state.orderCode = createOrderCode();
      orderNodes.forEach((node) => { node.textContent = state.orderCode; });
      if (hiddenOrder) hiddenOrder.value = state.orderCode;
    };

    const setStage = (step, { focus = true } = {}) => {
      state.step = step;
      stages.forEach((stage) => {
        const active = Number(stage.dataset.wvCheckoutStage) === step;
        stage.hidden = !active;
        stage.classList.toggle('is-active', active);
      });
      if (step > 0) {
        ensureOrder();
        saveState(state);
      }
      if (checkoutMap) checkoutMap.hidden = step === 0;
      checkoutMapSteps.forEach((item) => {
        const itemStep = Number(item.dataset.wvCheckoutMapStep);
        const active = itemStep === step;
        item.classList.toggle('is-active', active);
        if (active) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      updatePurchaseCtas();
      if (emailInput && state.email && emailInput.value !== state.email) emailInput.value = state.email;
      if (emailSummary) emailSummary.textContent = state.email || '—';
      if (focus) {
        const active = stages.find((stage) => Number(stage.dataset.wvCheckoutStage) === step);
        window.setTimeout(() => active?.querySelector('[data-wv-stage-heading]')?.focus({ preventScroll: true }), 30);
      }
    };

    const startCheckout = () => {
      const saved = readState();
      if (saved?.completed) {
        clearState();
        state = { orderCode: '', email: '', step: 0, startedAt: Date.now() };
      } else if (saved?.email) {
        state = saved;
      }
      if (!state.startedAt) state.startedAt = Date.now();
      ensureOrder();
      const resumeStep = state.email ? Math.min(Math.max(Number(state.step) || 1, 1), 3) : 1;
      setStage(resumeStep);
    };

    root.addEventListener('watchva:start-purchase', startCheckout);
    root.querySelector('[data-wv-checkout-start]')?.addEventListener('click', startCheckout);

    root.querySelectorAll('[data-wv-copy]').forEach((button) => {
      button.addEventListener('click', async () => {
        const value = button.getAttribute('data-wv-copy') || '';
        if (!value) return;
        const original = button.textContent;
        try {
          await navigator.clipboard.writeText(value);
          button.textContent = 'Copied ✓';
        } catch (_) {
          const input = document.createElement('textarea');
          input.value = value;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
          button.textContent = 'Copied ✓';
        }
        window.setTimeout(() => { button.textContent = original; }, 1600);
      });
    });



    copyReturnButton?.addEventListener('click', async () => {
      ensureOrder();
      const url = new URL(window.location.href);
      url.searchParams.set('watchva_order', state.orderCode);
      url.hash = 'watchva-purchase';
      const original = copyReturnButton.textContent;
      try {
        await navigator.clipboard.writeText(url.toString());
        copyReturnButton.textContent = 'Return link copied ✓';
      } catch (_) {
        const input = document.createElement('textarea');
        input.value = url.toString();
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
        copyReturnButton.textContent = 'Return link copied ✓';
      }
      window.setTimeout(() => { copyReturnButton.textContent = original; }, 1800);
    });

    root.querySelector('[data-wv-checkout-next="2"]')?.addEventListener('click', () => {
      if (!emailInput) return;
      if (!emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }
      state.email = emailInput.value.trim();
      state.step = 2;
      saveState(state);
      setStage(2);
    });

    root.querySelector('[data-wv-checkout-paid]')?.addEventListener('click', () => {
      if (!ready) return;
      state.step = 3;
      state.paymentReturnedAt = Date.now();
      saveState(state);
      setStage(3);
    });

    root.querySelectorAll('[data-wv-checkout-back]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(button.dataset.wvCheckoutBack);
        if (!Number.isFinite(step)) return;
        setStage(step);
      });
    });

    emailInput?.addEventListener('input', () => {
      state.email = emailInput.value.trim();
      if (state.step > 0) saveState(state);
      if (emailSummary) emailSummary.textContent = state.email || '—';
    });

    let submitting = false;
    form?.addEventListener('submit', (event) => {
      if (submitting) {
        event.preventDefault();
        return;
      }
      if (!emailInput?.checkValidity()) {
        event.preventDefault();
        setStage(1);
        emailInput?.reportValidity();
        return;
      }
      if (!referenceInput?.checkValidity()) {
        event.preventDefault();
        setStage(3);
        referenceInput?.reportValidity();
        return;
      }

      state.email = emailInput.value.trim();
      state.step = 3;
      ensureOrder();
      saveState(state);

      const submittedAt = new Date().toISOString();
      if (hiddenSubmitted) hiddenSubmitted.value = submittedAt;
      if (hiddenOrder) hiddenOrder.value = state.orderCode;
      if (hiddenBody) {
        const amount = root.querySelector('.wv-purchase-price')?.textContent?.trim() || '';
        hiddenBody.value = [
          'WATCHVA PAYMENT TO VERIFY',
          `Order: ${state.orderCode}`,
          'Product: Too Much in My Head',
          `Customer email: ${state.email}`,
          amount ? `Expected amount: ${amount}` : '',
          `GCash reference: ${referenceInput.value.trim()}`,
          `Submitted at: ${submittedAt}`
        ].filter(Boolean).join('\n');
      }
      submitting = true;
      if (submitButton) {
        if (!submitButton.dataset.wvDefaultLabel) submitButton.dataset.wvDefaultLabel = submitButton.textContent.trim();
        submitButton.disabled = true;
        submitButton.textContent = 'Sending payment details…';
        submitButton.setAttribute('aria-busy', 'true');
      }
    });

    root.querySelector('[data-wv-checkout-new]')?.addEventListener('click', () => {
      clearState();
      updatePurchaseCtas();
      window.location.href = window.location.pathname + window.location.search;
    });

    if (formState === 'success' && successStage) {
      const completed = readState();
      const successEmail = successStage.querySelector('[data-wv-success-email]');
      const successOrder = successStage.querySelector('[data-wv-success-order]');
      if (successEmail && completed?.email) successEmail.textContent = completed.email;
      if (successOrder && completed?.orderCode) successOrder.textContent = completed.orderCode;
      if (completed) {
        completed.completed = true;
        completed.completedAt = Date.now();
        saveState(completed);
      }
      if (checkoutMap) checkoutMap.hidden = true;
      updatePurchaseCtas();
      return;
    }

    if (formState === 'error') {
      if (state.email && emailInput) emailInput.value = state.email;
      setStage(3, { focus: false });
      return;
    }



    const requestedOrder = new URLSearchParams(window.location.search).get('watchva_order');
    if (requestedOrder && state.orderCode && requestedOrder === state.orderCode && !state.completed) {
      const resumeStep = state.email ? Math.min(Math.max(Number(state.step) || 1, 1), 3) : 1;
      setStage(resumeStep, { focus: false });
      window.setTimeout(() => scrollToTarget(root), 30);
      return;
    }

    if (state.email && state.step > 0) {
      const resumeStep = Math.min(Math.max(Number(state.step) || 1, 1), 3);
      setStage(resumeStep, { focus: false });
    } else {
      setStage(0, { focus: false });
    }
  }

  function initHero(root) {
    if (!root || root.dataset.wvHeroReady === 'true') return;
    root.dataset.wvHeroReady = 'true';
    const book = root.querySelector('[data-wv-book-object]');
    if (!book || reduceMotion.matches || !finePointer.matches) return;

    let raf = 0;
    let px = 0;
    let py = 0;

    const paint = () => {
      raf = 0;
      const rx = py * -1.35;
      const ry = px * 2.8 - 5.5;
      const lift = (Math.abs(px) + Math.abs(py)) * 1.5;
      book.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg) rotateZ(${0.7 + px * 0.25}deg) translateY(${-lift}px)`;
    };

    const onMove = (event) => {
      const rect = root.getBoundingClientRect();
      px = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      py = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
      if (!raf) raf = requestAnimationFrame(paint);
    };

    const onLeave = () => {
      px = 0;
      py = 0;
      book.style.transform = '';
    };

    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerleave', onLeave, { passive: true });
    root._wvHeroDestroy = () => {
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }

  function initRoot(root) {
    initAnchors(root);
    initReveal(root);
    if (root.matches('[data-wv-header]')) initHeader(root);
    if (root.matches('[data-wv-quickstart]')) initQuickStart(root);
    if (root.matches('[data-wv-preview-section]')) initPreviewRail(root);
    if (root.matches('[data-wv-faq]')) initFaq(root);
    if (root.matches('[data-wv-purchase]')) initPurchase(root);
    if (root.matches('[data-wv-hero]')) initHero(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll('.wv-home').forEach(initRoot);
    if (scope.matches?.('.wv-home')) initRoot(scope);
    updatePurchaseCtas();
  }

  function destroyRoot(root) {
    root?._wvDestroy?.();
    root?._wvRevealObserver?.disconnect?.();
    root?._wvHeroDestroy?.();
    root?._wvPreviewDestroy?.();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('.wv-home')) destroyRoot(event.target);
    event.target.querySelectorAll?.('.wv-home').forEach(destroyRoot);
  });
})();
